// Stage timings are optional for older results; never invent a past duration.
// Estimated overall Stage-1 work, not elapsed time. Use phase milestones rather
// than the queue percentage, whose scale differs between standalone/full jobs.
// Most of the range is reserved for completed sensitivity scenarios. No timers
// advance the bar while the worker has not reported any new work.
function firstStageProgress(job) {
  if (job.status === 'queued') return { progress: 0 };
  if (job.status === 'completed') return { progress: 100 };
  const match = /^sensitivity:(\d+)\/(\d+):/.exec(job.message || '');
  if (match && Number(match[2]) > 0 && Number(match[1]) <= Number(match[2])) {
    return { progress: 30 + 65 * Number(match[1]) / Number(match[2]) };
  }
  const phase = String(job.message || '').split(':')[0];
  return { progress: ({ preparation: 2, reference_design: 5, reference_generation: 5,
    sweep: 20, sensitivity: 30, stage1_ready: 98, report: 98, completed: 99 })[phase] ?? 0 };
}

export function sizingStageJobs(job) {
  if (!job) return [];
  const result = job.result || {};
  const s1 = result.stage1;
  const s2 = result.stage2;
  const execution = result.execution || {};
  const second = Boolean(s1 && ['running', 'completed'].includes(s2?.status));
  const pct = Math.max(0, Math.min(100, Number(job.progress) || 0));
  const first = s1
    ? { status: 'completed', progress: 100, message: 'completed', ...execution.stage1 }
    : { ...job, ...firstStageProgress(job) };
  // Legacy stage-1-only jobs still have valid execution timestamps.
  if (s1 && !second && !execution.stage1) {
    Object.assign(first, { started_at: job.started_at, finished_at: job.finished_at, created_at: job.created_at });
  }
  const next = second ? { ...job, ...execution.stage2,
    progress: job.status === 'completed' ? 100 : Math.max(0, Math.min(99, (pct - 36) / 64 * 100)),
    message: job.message === 'preparation' || job.message === 'stage1_ready' ? 'stage2_preparation' : job.message,
  } : { status: s2?.status === 'skipped_invalid' ? 'blocked' : 'waiting', progress: 0 };
  return [{ stage: 1, ...first }, { stage: 2, ...next }];
}

export function continuationResult(job) {
  if (!job?.result?.stage1) return null;
  return { input_config: job.result.input_config, stage1: job.result.stage1,
    stage2: { status: 'running', candidates: [] },
    execution: { stage1: job.result.execution?.stage1 || {
      started_at: job.started_at, finished_at: job.finished_at,
    } },
  };
}

// A status-only response must not clear a continuation's saved stage-1 result.
export function mergeSizingJob(previous, incoming) {
  if (!incoming) return incoming;
  return incoming.id === previous?.id && !incoming.result && previous.result
    ? { ...incoming, result: previous.result } : incoming;
}

export function sensitivityEffects(sensitivity) {
  const base = sensitivity?.base?.npv_eur;
  if (!Number.isFinite(base)) return {};
  const rows = (sensitivity.scenarios || []).filter(r => Number.isFinite(r.npv_at_base_k))
    .map(r => ({ ...r, change: r.npv_at_base_k - base }));
  return { downside: rows.filter(r => r.change < -0.01).sort((a, b) => a.change - b.change)[0],
    upside: rows.filter(r => r.change > 0.01).sort((a, b) => b.change - a.change)[0] };
}
